import React from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeft,
  ArrowRight,
  Clock,
  Sparkles,
  FolderTree,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import Breadcrumbs from '../components/Breadcrumbs';

/**
 * Tier-3 property-management content piece targeting "organize property
 * management documents" and the property/unit/tenant filing problem. This is
 * an open lane with no single dominant authority, so the angle is practical:
 * a Property > Unit/Tenant > category structure (Leases, Inspections,
 * Maintenance, Financials), a copyable folder tree and naming pattern, and a
 * working Sortio prompt that routes lease packets, inspection reports,
 * maintenance invoices, and owner statements off the content of each file.
 * Sortio is positioned as the layer that names and files raw local documents
 * before they go into a property-management system, not as a replacement for
 * one.
 */

const PUBLISHED = '2026-06-05';
const UPDATED = '2026-06-05';
const CANONICAL = 'https://www.getsortio.com/blog/organize-property-management-documents';

const FAQ = [
  {
    question: 'How do you organize property management documents by property and tenant?',
    answer:
      'Use a three-level structure: property at the top, then unit or tenant, then a small fixed set of document categories (Leases, Inspections, Maintenance, Financials). Every file lands in exactly one place, so anyone on the team can find the current lease for Unit 4B or last quarter\'s maintenance invoices for a building without guessing. Sortio reads the content of each scanned lease, inspection report, or vendor invoice and routes it into that structure automatically, which is what keeps the system consistent once more than one person is filing.',
  },
  {
    question: 'What folder structure works best for a property management company?',
    answer:
      'Property > Unit or Tenant > Category. The top level is the building or single-family address. The second level is the unit number for multifamily, or the tenant name for single-family and short leases. The third level is a fixed category list: Leases, Inspections, Maintenance, Financials, and Notices. Keep the category list short and identical across every property so the layout is predictable. A consistent structure is also the thing that makes a shared drive survivable when staff turns over.',
  },
  {
    question: 'What is a good file naming convention for leases and inspections?',
    answer:
      'Date first, then property and unit, then document type, so files sort chronologically and are scannable by eye. A lease becomes 2026-06-01_OakSt_Unit4B_Lease_Smith.pdf and a move-in inspection becomes 2026-06-01_OakSt_Unit4B_Inspection_MoveIn.pdf. Maintenance invoices carry the vendor and amount: 2026-05-18_OakSt_Unit4B_Maintenance_ABCPlumbing_$340.pdf. The point is that the filename alone tells you the property, the unit, the type, and when, without opening anything.',
  },
  {
    question: 'Can Sortio handle scanned leases and inspection reports?',
    answer:
      'Yes, when the scan has a text layer. Many property documents are scans (signed leases, paper inspection checklists) with no useful filename and no metadata. Sortio reads the text layer of each PDF; scan as searchable PDF, or add a layer to image-only scans first with a free tool like OCRmyPDF. It then reads the content with an LLM to identify the property, unit, tenant, and document type and files it accordingly. An LLM is robust to the OCR noise that breaks regex-based tools, which is why this holds up across a real mix of scanned property paperwork.',
  },
  {
    question: 'Does Sortio replace AppFolio, Buildium, or DoorLoop?',
    answer:
      'No, and it is not meant to. Property-management platforms are systems of record for leases, accounting, and tenant communication. Sortio is the layer in front of them: it takes the raw local files that pile up in Downloads and shared folders (scanned signed leases, inspection photos and reports, vendor invoices, owner statements) and gets them named and filed consistently before you upload them into your platform or your accounting system. Think of it as the intake and naming step, not a replacement for your management software.',
  },
  {
    question: 'How do you keep new documents filed automatically?',
    answer:
      'On Sortio Pro ($14.99/month or $99/year) you can point a watch folder at wherever documents land (a scans folder, a shared Downloads folder, the place your scanner saves) and apply the same routing prompt to every new arrival. Leave it in Preview mode for the first week so Sortio queues proposed moves and notifies you instead of applying them, then switch to Apply once the classifications look right. From then on, new leases and invoices file themselves by property and unit without anyone opening the app.',
  },
  {
    question: 'How do you batch organize a backlog across many properties at once?',
    answer:
      'Point Sortio at the folder holding the backlog and give it one rule that covers every document type and every property. Sortio reads each file, identifies the property and unit from the content, picks the right category, renames to your template, and runs a Preview so you can confirm the whole batch before anything moves. A multi-property backlog that would take days to sort by hand goes through in one pass, and nothing is destructive because the original copies stay in a 30-day backup.',
  },
];

const OrganizePropertyManagementDocumentsBlogPage = () => {
  const breadcrumbItems = [
    { label: 'Blog', href: null },
    { label: 'Organize property management documents', href: null },
  ];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.getsortio.com/' },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: CANONICAL },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Organize property management documents',
        item: CANONICAL,
      },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'How to Organize Property Management Documents by Property and Tenant',
    description:
      'A practical filing system for property managers: Property > Unit/Tenant > category (Leases, Inspections, Maintenance, Financials). A copyable folder tree, a naming pattern, and a working Sortio prompt that files leases, inspections, and invoices by content.',
    url: CANONICAL,
    datePublished: PUBLISHED,
    dateModified: UPDATED,
    about: {
      '@type': 'SoftwareApplication',
      name: 'Sortio',
      url: 'https://www.getsortio.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'CMG Labs LLC',
      url: 'https://www.getsortio.com',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };

  return (
    <div className="min-h-screen bg-black text-apple-100">
      <SEOHead
        title="How to Organize Property Management Documents by Property and Tenant"
        description="Stop losing leases and inspection reports in a shared folder. Set up a Property > Unit/Tenant > category structure, name files consistently, and let Sortio file leases, inspections, and maintenance invoices by content. Working prompt included."
        canonical={CANONICAL}
        schema={[breadcrumbSchema, articleSchema, faqSchema]}
        keywords="organize property management documents, property management file organization, organize leases by property, organize tenant documents, property management folder structure, file naming convention leases, organize inspection reports, batch organize documents per property"
        lastModified={UPDATED}
      />

      <div className="container mx-auto px-6 max-w-3xl py-16 lg:py-24">
        <Breadcrumbs items={breadcrumbItems} />

        <Link
          to="/articles"
          className="inline-flex items-center text-apple-300 hover:text-sortio-primary mb-8 transition-colors group"
        >
          <ChevronLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
          <span>All posts</span>
        </Link>

        <header className="mb-10">
          <span className="inline-block px-3 py-1 bg-sortio-primary/10 text-sortio-primary text-xs font-medium rounded-lg uppercase tracking-wider mb-4">
            Blog
          </span>
          <h1 className="text-4xl lg:text-5xl font-semibold text-apple-50 mb-6 leading-tight">
            How to Organize Property Management Documents by Property and Tenant
          </h1>
          <div className="flex items-center text-apple-400 text-sm gap-4">
            <span className="flex items-center">
              <Clock className="mr-2" size={14} />
              Published {new Date(PUBLISHED).toLocaleDateString()}
            </span>
            <span>Updated {new Date(UPDATED).toLocaleDateString()}</span>
            <span>9 min read</span>
          </div>
        </header>

        <article className="prose-content space-y-6 text-apple-200 leading-relaxed">
          <p className="text-lg">
            A property manager\'s shared folder is where leases go to disappear. Signed leases
            named scan_0421.pdf, inspection reports named IMG_4471.jpg, vendor invoices named
            invoice.pdf, owner statements named statement(3).pdf. Every document for every
            property is in there somewhere, but finding the current lease for one unit means
            opening files one at a time until you get lucky. The information you need (which
            property, which unit, which tenant, what kind of document) is inside each file, and
            none of it is in the name.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            How to organize property management documents by property and tenant
          </h2>

          <p>
            Organize property management documents in three levels: property at the top, then
            unit or tenant, then a small fixed set of categories (Leases, Inspections,
            Maintenance, Financials). Sortio reads the content of each lease, inspection report,
            or invoice and files it into that structure automatically, so the system stays
            consistent even when several people are adding documents. That single decision, a
            predictable place for every file, is what separates a folder you can run a portfolio
            from and a folder you dread opening.
          </p>

          <p>
            The rest of this post is the full setup: the exact folder tree and naming pattern to
            copy, how a content-reading tool differs from a manual sort or a rule engine, a
            working Sortio prompt you can paste in, and how to keep new documents filing
            themselves once the backlog is clean. If you manage rentals professionally, the{' '}
            <Link to="/for-property-management" className="text-sortio-primary hover:underline">
              Sortio for property management
            </Link>{' '}
            page covers the same workflow with the feature detail.
          </p>

          <div className="bg-sortio-primary/10 border border-sortio-primary/20 rounded-2xl p-6 my-8 not-prose">
            <div className="flex items-start gap-3">
              <FolderTree className="text-sortio-primary flex-shrink-0 mt-0.5" size={20} />
              <div>
                <h3 className="font-medium text-apple-50 mb-2">The short version</h3>
                <p className="text-apple-300 text-sm leading-relaxed">
                  Use Property {'>'} Unit/Tenant {'>'} category (Leases, Inspections, Maintenance,
                  Financials). Name files date-first:
                  {' 2026-06-01_OakSt_Unit4B_Lease_Smith.pdf'}. Sortio reads the text inside each
                  searchable-PDF lease, inspection report, and vendor invoice (OCRmyPDF adds a
                  text layer to image-only scans), identifies the property and unit, applies the
                  template, and routes the file. One prompt covers every document type, and a watch folder keeps new
                  arrivals filed without anyone touching them.
                </p>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            The folder structure: Property, then Unit or Tenant, then category
          </h2>

          <p>
            The structure that holds up across a growing portfolio is three levels deep and no
            deeper. Top level is the building or single-family address. Second level is the unit
            number for multifamily, or the tenant name for single-family and short-term leases.
            Third level is a fixed, identical-everywhere category list. Here is the tree:
          </p>

          <pre className="bg-apple-700/30 rounded-lg p-4 overflow-x-auto text-sm text-apple-100 not-prose">
{`Properties/
  142-Oak-St/
    Unit-4B/
      Leases/
      Inspections/
      Maintenance/
      Financials/
      Notices/
    Unit-2A/
      Leases/
      Inspections/
      Maintenance/
      Financials/
      Notices/
    _Building/                # shared docs: insurance, HOA, taxes
      Financials/
      Maintenance/
  88-Pine-Ave/                # single-family, keyed by tenant
    Tenant-Garcia/
      Leases/
      Inspections/
      Maintenance/
      Financials/
      Notices/`}
          </pre>

          <p>
            Two details make this work. First, the category list (Leases, Inspections,
            Maintenance, Financials, Notices) is identical under every unit and tenant, so the
            layout is predictable and muscle-memory finds files without thinking. Second, the
            <code> _Building</code> folder holds documents that belong to the whole property
            rather than one unit (master insurance, property tax, common-area maintenance), so
            they do not get orphaned at the top level. Keep the category list short. Five
            categories you actually use beats fifteen that fragment your filing. The Financials
            category earns its place for a reason: the IRS advises keeping property records{' '}
            <a
              href="https://www.irs.gov/businesses/small-businesses-self-employed/how-long-should-i-keep-records"
              className="text-sortio-primary hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              until the period of limitations expires for the year you dispose of the property
            </a>
            , so for a building you hold for a decade, the invoices and owner statements you file
            today still have to be findable long after the tenant has moved out.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            The naming pattern: date, property, unit, type
          </h2>

          <p>
            The folder tells you the property and unit; the filename should still stand on its
            own so a file is identifiable even after it gets copied into an email or an
            accounting system. The pattern that works:
          </p>

          <pre className="bg-apple-700/30 rounded-lg p-4 overflow-x-auto text-sm text-apple-100 not-prose">
{`{YYYY-MM-DD}_{Property}_{Unit}_{Type}_{Detail}.ext

2026-06-01_OakSt_Unit4B_Lease_Smith.pdf
2026-06-01_OakSt_Unit4B_Inspection_MoveIn.pdf
2026-05-18_OakSt_Unit4B_Maintenance_ABCPlumbing_$340.pdf
2026-05-31_OakSt_Building_Financials_OwnerStatement.pdf
2026-05-12_PineAve_Garcia_Notice_LateRent.pdf`}
          </pre>

          <p>
            Date first (ISO YYYY-MM-DD) so the folder sorts chronologically in Finder or Explorer
            with no custom view. Property and unit next so a file dropped anywhere still says
            where it belongs. Type is the same short word as the category folder (Lease,
            Inspection, Maintenance, Financials, Notice). The trailing Detail disambiguates:
            tenant surname for a lease, MoveIn or Quarterly for an inspection, vendor and amount
            for a maintenance invoice. For a deeper treatment of naming rules and how to keep a
            whole team using them, see{' '}
            <Link to="/blog/enforce-file-naming-convention" className="text-sortio-primary hover:underline">
              how to enforce a file naming convention
            </Link>
            .
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Manual sorting vs a rule engine vs reading the content
          </h2>

          <p>
            There are three honest ways to file property documents, and they fail in different
            places. It is worth being clear about which one you are actually choosing.
          </p>

          <p>
            <strong>Manual sorting.</strong> You open each scan, read it, rename it, and drag it
            to the right unit folder. This is accurate because a human is reading the content,
            and it is exactly why nobody keeps it up. A leasing season generates hundreds of
            documents, and manual filing is the first thing dropped when you are busy. The
            backlog grows, and the structure you designed exists only on paper.
          </p>

          <p>
            <strong>A rule engine.</strong> Tools like Hazel (macOS only) or File Juggler
            (Windows only) match on the filename or on a regex against the PDF text layer and
            move files accordingly. This is fast and free to run once configured, and it is great
            when the input is predictable. It breaks on property documents specifically because
            the input is not predictable: a scanned signed lease has no clean text to match, the
            unit number appears in a different spot on every vendor\'s invoice, and OCR text
            drifts between scans of the same form. You end up writing and debugging a regex per
            document type per property, which does not scale. macOS Finder, for the record, has no
            built-in rule engine at all (you would script Automator or Folder Actions), so the
            rule-engine path is not even free on a Mac out of the box.
          </p>

          <p>
            <strong>Reading the content with AI.</strong> Sortio opens each file, reads the text
            layer (add one to image-only scans first with a free tool like OCRmyPDF), and uses
            an LLM to identify the property, unit, tenant, and document type from what the page
            actually says. It is robust to the OCR noise and layout variation that break a regex,
            and it needs one prompt instead of a rule library. The trade-off is that each AI sort
            draws from your AI allowance and inference is slower than a regex. The right answer for most
            property managers is to use AI for the messy intake (scanned leases, varied invoices)
            and, for any high-volume single-source flow that is genuinely uniform, promote it to a
            deterministic rule. More on that split in{' '}
            <Link
              to="/blog/ai-sort-vs-rule-builder-which-to-use"
              className="text-sortio-primary hover:underline"
            >
              AI Sort vs Rule Builder
            </Link>
            .
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            A working Sortio prompt for property documents
          </h2>

          <p>
            Drop this into the Sortio prompt box, point it at the folder where your scans and
            downloads accumulate, and run Preview before applying. Adjust the property and unit
            names to match yours.
          </p>

          <pre className="bg-apple-700/30 rounded-lg p-4 overflow-x-auto text-sm text-apple-100 not-prose">
{`Read each document and identify which property and unit
(or tenant) it belongs to from the content (address,
unit number, tenant name). Then classify it as one of:
lease, inspection, maintenance, financial, notice, or
other.

File everything under:
~/Properties/{Property}/{UnitOrTenant}/{Category}/

Leases (signed or unsigned rental agreements) go to the
Leases/ folder, renamed:
{YYYY-MM-DD}_{Property}_{Unit}_Lease_{TenantSurname}.pdf

Inspections (move-in, move-out, periodic checklists or
reports) go to Inspections/, renamed:
{YYYY-MM-DD}_{Property}_{Unit}_Inspection_{Kind}.pdf
Kind is one of: MoveIn, MoveOut, Quarterly, Annual.

Maintenance (work orders, vendor invoices, repair
receipts) go to Maintenance/, renamed:
{YYYY-MM-DD}_{Property}_{Unit}_Maintenance_{Vendor}_{Amount}.pdf

Financials (owner statements, rent ledgers, tax docs,
insurance) go to Financials/, renamed:
{YYYY-MM-DD}_{Property}_{Unit}_Financials_{Type}.pdf
If a financial doc is for the whole building, use
Unit = Building.

Notices (late rent, lease violation, entry, renewal)
go to Notices/, renamed:
{YYYY-MM-DD}_{Property}_{Unit}_Notice_{Type}.pdf

Anything you cannot confidently place goes to
~/Properties/_Inbox/ untouched.

Use short property names (OakSt not 142 Oak Street).
Dates in YYYY-MM-DD. Amounts as $X.XX.`}
          </pre>

          <p>
            Click Preview. Sortio shows the proposed name, target folder, and the field values it
            read for every file before anything moves. If a unit number reads wrong on a messy
            scan, untick that file so it stays where it is, or refine the prompt and run Preview
            again; nothing moves until you apply. Apply commits the moves. Nothing is
            destructive, and Sortio keeps backup archives of your most recent sorts, so a
            misfile is one in-app undo away rather than a lost document.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Clearing the backlog, then keeping it clean
          </h2>

          <p>
            Most property managers start with a backlog: a shared drive or scans folder with
            years of mixed documents across every property. Point Sortio at it, run the prompt
            above, and let Preview show you the full proposed reorganization before committing.
            A multi-property backlog goes through in one pass instead of the days a manual sort
            would take. If the shared drive itself is the mess, the broader playbook is in{' '}
            <Link
              to="/blog/clean-up-messy-shared-drive"
              className="text-sortio-primary hover:underline"
            >
              how to clean up a messy shared drive
            </Link>
            .
          </p>

          <p>
            Once the backlog is filed, keep it filed automatically. On Sortio Pro ($14.99/month
            or $99/year) you can turn the same prompt into a watch folder pointed at wherever new
            documents land (your scanner\'s output folder, a shared Downloads folder, the place
            leasing staff drop files). For the first week, leave the watch in Preview mode:
            Sortio queues the proposed moves and notifies you instead of applying them, so you can
            confirm the AI is placing things correctly. After a week of clean previews, switch to
            Apply, and from then on every new lease, inspection, and invoice files itself by
            property and unit within seconds of arriving. For a team, Sortio Teams ($29/seat/month)
            adds shared rules and an admin console so everyone files the same way.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Where Sortio fits next to AppFolio, Buildium, or DoorLoop
          </h2>

          <p>
            Sortio is not a replacement for a property-management platform, and you should not
            try to use it as one. Platforms like AppFolio, Buildium, and DoorLoop are systems of
            record: they hold the lease of truth, run the accounting, and handle tenant
            communication. The problem they do not solve is the pile of raw local files that
            never made it in cleanly: the scanned signed lease still in Downloads, the inspection
            photos on a phone export, the vendor invoice in an email attachment, the owner
            statement saved as statement(3).pdf.
          </p>

          <p>
            Sortio is the intake and naming layer in front of that system. It takes those raw
            local files, reads them, names them to your convention, and files them into a clean
            property and unit structure, ready to upload or archive. The platform stays the
            source of truth; Sortio makes sure the documents arrive named and organized instead
            of as a folder of scan_0421.pdf. The two are complementary, and Sortio runs locally
            on your machine, so the files are handled before they go anywhere.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Privacy and local processing
          </h2>

          <p>
            Property documents are sensitive: leases carry tenant names, Social Security numbers,
            and bank details; notices and financials carry private information about real people.
            Sortio supports local-only processing through Ollama, where the LLM runs on your own
            machine (Llama 3, Mistral) and no file content leaves it. The managed AI option is
            faster and more accurate, or you can bring your own API key; for the full trade-off
            between the two, see{' '}
            <Link
              to="/blog/local-ai-vs-cloud-ai-file-organization"
              className="text-sortio-primary hover:underline"
            >
              local AI vs cloud AI for file organization
            </Link>
            . Whichever you pick, the preview-before-apply step and the 30-day backup mean you are
            never one bad classification away from a lost or misfiled tenant record.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            FAQ
          </h2>

          <div className="space-y-4 not-prose">
            {FAQ.map((f, i) => (
              <div key={i} className="bg-apple-700/20 rounded-xl p-6">
                <h3 className="font-medium text-apple-50 mb-3">{f.question}</h3>
                <p className="text-apple-300 leading-relaxed">{f.answer}</p>
              </div>
            ))}
          </div>
        </article>

        {/* Internal links */}
        <section className="mt-16 pt-8 border-t border-apple-700/30">
          <h2 className="text-xl font-semibold text-apple-50 mb-4">Keep reading</h2>
          <div className="grid gap-3">
            <Link
              to="/blog/enforce-file-naming-convention"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                How to enforce a file naming convention across a team
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
            <Link
              to="/blog/clean-up-messy-shared-drive"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                How to clean up a messy shared drive
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
            <Link
              to="/blog/batch-organize-documents-per-client"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                How to batch organize documents per client automatically
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
          </div>
        </section>

        <section className="mt-12">
          <div className="bg-gradient-to-r from-sortio-primary/10 to-sortio-secondary/10 rounded-2xl p-8 border border-sortio-primary/20">
            <div className="flex items-center mb-4">
              <Sparkles className="text-sortio-primary mr-3" size={24} />
              <h2 className="text-2xl font-semibold text-apple-50">File a backlog of property documents</h2>
            </div>
            <p className="text-apple-300 mb-6 leading-relaxed">
              The free tier includes a one-time AI trial allowance. Point Sortio at a folder of
              unnamed leases, inspections, and invoices, run Preview, and see the proposed
              property and unit structure before applying. No credit card required.
            </p>
            <Link
              to="/download"
              className="inline-flex items-center px-6 py-3 bg-sortio-primary hover:bg-sortio-primary/90 text-white font-medium rounded-xl transition-colors duration-200"
            >
              Download Sortio
              <ArrowRight className="ml-2" size={16} />
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default OrganizePropertyManagementDocumentsBlogPage;
